import React, { useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { IProtectedMinimalLayoutProps } from './ProtectedMinimalLayout.types'
import * as S from './ProtectedMinimalLayout.styles'

import { ProfileMenu } from '@/components/common/ProfileMenu'
import { FeedbackButton } from '@/components/common/FeedbackButton'
import { FeedbackModal } from '@/components/common/FeedbackModal'
import { useUI } from '@/contexts/UIContext'
import { useResponsive } from '@/hooks/useResponsive'
import { useLogout } from '@/hooks/auth/useLogout'
import { selectUserViewType, selectUserProfile } from '@/store/selectors/authSelectors'
import { clearAllTokens } from '@/utils/auth/tokenManager'
import { clearAuth } from '@/store/slices/authSlice'
import { asyncToast } from '@/components/ui/Toast/asyncToast'
import { NavigationRoutes } from '@/router/navigation.types'
import type { RootState } from '@/store'

export function ProtectedMinimalLayout({ children }: IProtectedMinimalLayoutProps) {
    // Redux State (exact same pattern as AppHeader)
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const currentView = useSelector(selectUserViewType)
    const userProfile = useSelector(selectUserProfile)
    const isMockAuth = useSelector((state: RootState) => state.auth.isMockAuth)

    // UI Context for Feedback Modal
    const { showFeedbackModal, setShowFeedbackModal } = useUI()

    // Responsive Hook
    const { isMobile } = useResponsive()

    // Logout Hook
    const logoutMutation = useLogout()

    // Logout Logic (exact same as AppHeader)
    const handleLogout = useCallback(() => {
        if (isMockAuth) {
            // For mock auth, handle logout locally without API call
            clearAllTokens()
            dispatch(clearAuth())
            queryClient.clear()
            toast.success('Successfully logged out')
            navigate(NavigationRoutes.LOGIN, { replace: true })
        } else {
            // For real auth, use the logout hook with asyncToast
            asyncToast({
                id: 'logout',
                promise: logoutMutation.mutateAsync(),
                msgs: {
                    loading: 'Logging out...',
                    success: 'Successfully logged out',
                    error: 'Failed to logout',
                },
            })
        }
    }, [isMockAuth, dispatch, queryClient, navigate, logoutMutation])

    // User Object Construction (exact same as AppHeader)
    const user = {
        id: '1',
        fullName: userProfile?.preferredName || `${userProfile?.firstName} ${userProfile?.lastName}`,
        email: userProfile?.email || '',
        role: currentView || 'employee',
        avatarUrl: userProfile?.avatarUrl || undefined,
        avatarType: 'default' as const,
    }

    // Return null if no user data (same guard as AppHeader)
    if (!currentView || !userProfile) return null

    return (
        <S.LayoutContainer>
            {/* Floating UI Components - Top Right */}
            <S.FloatingUIContainer>
                {/* Feedback Button (same logic as AppHeader) */}
                <FeedbackButton onClick={() => setShowFeedbackModal(true)} isMobile={isMobile} />

                {/* Profile Menu (imported base component) - logout only mode */}
                <ProfileMenu user={user} onLogout={handleLogout} showOnlyLogout={true} />
            </S.FloatingUIContainer>

            {/* Main Content */}
            <S.ContentContainer>{children}</S.ContentContainer>

            {/* Feedback Modal (imported base component) */}
            <FeedbackModal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} />
        </S.LayoutContainer>
    )
}
